import React from "react";
import { format, parseISO, isValid } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarIcon } from "lucide-react";

/**
 * Branded date picker built on the shadcn Calendar.
 *  - value: ISO YYYY-MM-DD string or ""
 *  - onChange(iso: string)
 *  - minDate: ISO string (inclusive)
 *  - label, placeholder, testId
 *  - display format: "EEE, dd MMM yyyy"  -> "Mon, 19 Jul 2026"
 */
export default function DatePicker({
  value = "",
  onChange,
  minDate = "",
  maxDate = "",
  label,
  placeholder = "DD/MM/YYYY",
  testId = "datepicker",
  className = "",
  error = "",
  yearDropdown = false,
  fromYear,
  toYear,
}) {
  const [open, setOpen] = React.useState(false);
  const dateObj = value && isValid(parseISO(value)) ? parseISO(value) : null;
  const min = minDate && isValid(parseISO(minDate)) ? parseISO(minDate) : null;
  const max = maxDate && isValid(parseISO(maxDate)) ? parseISO(maxDate) : null;
  const display = dateObj ? format(dateObj, "dd/MM/yyyy") : "";

  let disabled;
  if (min && max) disabled = { before: min, after: max };
  else if (min) disabled = { before: min };
  else if (max) disabled = { after: max };

  const captionProps = yearDropdown
    ? {
        captionLayout: "dropdown-buttons",
        fromYear: fromYear ?? 1925,
        toYear: toYear ?? new Date().getFullYear() + 20,
        classNames: { caption_label: "hidden" },
      }
    : {};

  return (
    <div className={className}>
      {label && <div className="text-xs text-slate-500 mb-1">{label}</div>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            data-testid={testId}
            className={`w-full justify-start h-11 px-3 font-normal text-left ${
              error ? "border-red-400 focus-visible:ring-red-300" : ""
            } ${dateObj ? "text-[var(--brand-accent)]" : "text-slate-400"}`}
          >
            <CalendarIcon className="w-4 h-4 mr-2 text-[var(--brand-primary)]" />
            {display || placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start" data-testid={`${testId}-popover`}>
          <Calendar
            mode="single"
            selected={dateObj || undefined}
            onSelect={(d) => {
              if (!d) return;
              onChange?.(format(d, "yyyy-MM-dd"));
              setOpen(false);
            }}
            disabled={disabled}
            defaultMonth={dateObj || min || max || new Date()}
            initialFocus
            {...captionProps}
          />
        </PopoverContent>
      </Popover>
      {error && (
        <div className="text-xs text-red-500 mt-1" data-testid={`${testId}-error`}>{error}</div>
      )}
    </div>
  );
}
